/**
 * P420 -- what an exercise taught, and what happened to it.
 *
 * The gap this closes costs TWICE: two separate supervisory questions
 * ask for structured lessons learned, post-exercise and post-incident,
 * and before this the only home for one was a prose field on one kind
 * of drill.
 *
 * THE DISTINCTION THIS SURFACE EXISTS TO HOLD: `accepted` is not
 * `done`. Agreeing with a lesson changes nothing, and a register that
 * renders the two alike reports a bank as having learned from an
 * exercise when all it did was nod. So an accepted lesson that nobody
 * has incorporated is shown as WAITING, with how long it has waited,
 * and a supervisory check reads the same field.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { canRunOperations } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { useToast } from "shared/toast/ToastProvider";

import { useLessons, useMoveLesson } from "./continuityTestHooks";

const TONE: Record<string, string> = {
  open: "bg-slate-100 text-slate-600",
  accepted: "bg-rag-amber/15 text-rag-amber",
  declined: "bg-slate-100 text-slate-500",
  done: "bg-rag-green/15 text-rag-green",
};

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

/** The plan's grace for an accepted lesson, as the backend counts it
 *  (`shared.continuity.lessons.ACCEPTED_GRACE_DAYS`): from when it was
 *  raised. */
const LESSON_GRACE_DAYS = 90;

export function LessonsRegister() {
  const { t } = useTranslation();
  const toast = useToast();
  // Round 14 D4 -- the endpoints admit the Resilience Lead; so does
  // the screen. Lessons are her work.
  const isAdmin = canRunOperations(useMe().data);
  const lessons = useLessons();
  const move = useMoveLesson();
  const [reasonFor, setReasonFor] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const rows = Array.isArray(lessons.data) ? lessons.data : [];

  return (
    <section data-testid="lessons-register" className="space-y-1.5">
      <h2 className="font-mono text-[11px] uppercase tracking-wide text-slate-500">
        {t("continuityPlans.lessons.heading")}
      </h2>
      {rows.length === 0 ? (
        <p data-testid="lessons-empty" className="text-sm text-slate-500">
          {t("continuityPlans.lessons.none")}
        </p>
      ) : (
        <ul className="space-y-1">
          {rows.map((l) => {
            const waiting =
              l.status === "accepted" && l.incorporated_at == null
                ? daysSince(l.raised_at)
                : null;
            return (
              <li
                key={l.id}
                data-testid="lesson-row"
                data-status={l.status}
                className="rounded border border-paper-2 bg-white px-2.5 py-1.5 text-sm"
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <span
                    data-testid="lesson-status"
                    className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${
                      TONE[l.status] ?? ""
                    }`}
                  >
                    {t(`continuityPlans.lessons.status.${l.status}`)}
                  </span>
                  <span className="text-slate-800">{l.lesson}</span>
                  {waiting != null && (
                    <span
                      data-testid="lesson-waiting"
                      className="text-[11px] italic text-rag-amber"
                    >
                      {t(
                        waiting > LESSON_GRACE_DAYS
                          ? "continuityPlans.lessons.waitingOverdue"
                          : "continuityPlans.lessons.waiting",
                        { days: waiting, grace: LESSON_GRACE_DAYS },
                      )}
                    </span>
                  )}
                </div>
                {l.recommended_action && (
                  <p className="mt-0.5 text-xs text-slate-600">
                    {t("continuityPlans.lessons.actionLabel")} {l.recommended_action}
                  </p>
                )}
                {l.status === "declined" && l.declined_reason && (
                  <p className="mt-0.5 text-xs text-slate-500">
                    {t("continuityPlans.lessons.declinedLabel")} {l.declined_reason}
                  </p>
                )}
                {isAdmin && l.status !== "done" && (
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {l.status !== "accepted" && (
                      <button
                        type="button"
                        data-testid="lesson-accept"
                        onClick={() =>
                          move.mutate(
                            { id: l.id, status: "accepted" },
                            {
                              onSuccess: () =>
                                toast.push({
                                  tone: "success",
                                  message: t("continuityPlans.lessons.acceptedToast"),
                                }),
                            },
                          )
                        }
                        className="text-[11px] text-brand-brassDark hover:underline"
                      >
                        {t("continuityPlans.lessons.accept")}
                      </button>
                    )}
                    {l.status === "accepted" && (
                      <button
                        type="button"
                        data-testid="lesson-done"
                        onClick={() =>
                          move.mutate(
                            { id: l.id, status: "done" },
                            {
                              onSuccess: () =>
                                toast.push({
                                  tone: "success",
                                  message: t("continuityPlans.lessons.doneToast"),
                                }),
                            },
                          )
                        }
                        className="text-[11px] text-brand-brassDark hover:underline"
                      >
                        {t("continuityPlans.lessons.markDone")}
                      </button>
                    )}
                    {reasonFor === l.id ? (
                      <>
                        <input
                          aria-label={t("continuityPlans.lessons.reasonLabel")}
                          data-testid="lesson-reason"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder={t("continuityPlans.lessons.reasonLabel")}
                          className="w-64 rounded border border-slate-300 px-2 py-0.5 text-xs"
                        />
                        <button
                          type="button"
                          data-testid="lesson-decline-confirm"
                          disabled={!reason.trim()}
                          onClick={() =>
                            move.mutate(
                              {
                                id: l.id,
                                status: "declined",
                                declined_reason: reason.trim(),
                              },
                              {
                                onSuccess: () => {
                                  setReasonFor(null);
                                  setReason("");
                                },
                              },
                            )
                          }
                          className="text-[11px] text-rose-700 hover:underline disabled:opacity-40"
                        >
                          {t("continuityPlans.lessons.declineConfirm")}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        data-testid="lesson-decline"
                        onClick={() => setReasonFor(l.id)}
                        className="text-[11px] text-rose-700 hover:underline"
                      >
                        {t("continuityPlans.lessons.decline")}
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="max-w-3xl text-[11px] text-slate-500">
        {t("continuityPlans.lessons.note")}
      </p>
    </section>
  );
}
